/**
 * Velociraptor Settings API service
 *
 * Admin API client for Velociraptor service configuration and management.
 * All endpoints require EvidenX Admin role.
 */

import { apiClient, tokenStorage } from './api'

import { API_BASE_URL } from './api-base-url'

const DEFAULT_GUI_PORT = 8001
const DEFAULT_FRONTEND_PORT = 8889
const DEFAULT_API_PORT = 8000
const SECONDS_PER_HOUR = 3600
const SECONDS_PER_MINUTE = 60
const BYTES_PER_KIB = 1024
import type {
  VelociraptorConfig,
  ServiceStatusResponse,
  InstallResult,
  ActionResponse,
  ConnectivityTestResult,
  TestCollectorResult,
  PortStatus,
  UpgradeResult,
  TestEndpoint,
  UpdateConfigInput,
  InstallInput,
  TestCollectorInput,
  CheckPortsInput,
} from '../types/velociraptor-settings'

// =============================================================================
// CONFIGURATION API
// =============================================================================

/**
 * Get Velociraptor configuration
 */
export async function getConfig(): Promise<VelociraptorConfig> {
  return apiClient.get<VelociraptorConfig>('/admin/velociraptor/config')
}

/**
 * Update Velociraptor configuration
 */
export async function updateConfig(input: UpdateConfigInput): Promise<VelociraptorConfig> {
  return apiClient.put<VelociraptorConfig>('/admin/velociraptor/config', input)
}

// =============================================================================
// SERVICE STATUS & LIFECYCLE API
// =============================================================================

/**
 * Get service status
 */
export async function getStatus(): Promise<ServiceStatusResponse> {
  return apiClient.get<ServiceStatusResponse>('/admin/velociraptor/status')
}

/**
 * Install Velociraptor service
 */
export async function installService(input: InstallInput): Promise<InstallResult> {
  return apiClient.post<InstallResult>('/admin/velociraptor/install', input)
}

/**
 * Start Velociraptor service
 */
export async function startService(): Promise<ActionResponse> {
  return apiClient.post<ActionResponse>('/admin/velociraptor/start')
}

/**
 * Stop Velociraptor service
 */
export async function stopService(): Promise<ActionResponse> {
  return apiClient.post<ActionResponse>('/admin/velociraptor/stop')
}

/**
 * Upgrade Velociraptor service
 */
export async function upgradeService(): Promise<UpgradeResult> {
  return apiClient.post<UpgradeResult>('/admin/velociraptor/upgrade')
}

// =============================================================================
// CONNECTIVITY TESTING API
// =============================================================================

/**
 * Test connectivity to Velociraptor server
 */
export async function testConnection(): Promise<ConnectivityTestResult> {
  return apiClient.post<ConnectivityTestResult>('/admin/velociraptor/test-connection')
}

/**
 * Generate a test collector package
 */
export async function generateTestCollector(
  input: TestCollectorInput
): Promise<TestCollectorResult> {
  return apiClient.post<TestCollectorResult>('/admin/velociraptor/test-collector', input)
}

/**
 * Get test endpoints for collector testing
 */
export async function getTestEndpoints(): Promise<TestEndpoint[]> {
  return apiClient.get<TestEndpoint[]>('/admin/velociraptor/test-endpoints')
}

// =============================================================================
// PORT AVAILABILITY API
// =============================================================================

/**
 * Check port availability
 */
export async function checkPorts(input: CheckPortsInput): Promise<PortStatus[]> {
  return apiClient.post<PortStatus[]>('/admin/velociraptor/check-ports', input)
}

/**
 * Check default Velociraptor ports
 */
export async function checkDefaultPorts(): Promise<PortStatus[]> {
  return checkPorts({ ports: [DEFAULT_GUI_PORT, DEFAULT_FRONTEND_PORT, DEFAULT_API_PORT] })
}

/**
 * Download a generated collector binary
 * Triggers browser download of the file
 */
export async function downloadCollector(flowId: string): Promise<void> {
  // Use fetch to download binary and trigger browser download
  const token = tokenStorage.getAccessToken()
  const url = `${API_BASE_URL}/admin/velociraptor/collectors/${flowId}/download`

  // Create a temporary link to trigger download
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token ?? ''}`,
    },
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || 'Failed to download collector')
  }

  // Get filename from Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition')
  let filename = 'collector'
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/)
    if (match) {
      filename = match[1] ?? 'collector'
    }
  }

  // Create blob and trigger download
  const blob = await response.blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(downloadUrl)
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if service is configured
 */
export function isConfigured(config: VelociraptorConfig): boolean {
  return config.deploymentType !== 'not_configured'
}

/**
 * Check if service is running
 */
export function isRunning(status: ServiceStatusResponse): boolean {
  return status.isRunning && status.status === 'running'
}

/**
 * Format uptime for display
 */
export function formatUptime(uptimeSeconds: number | null): string {
  if (uptimeSeconds === null) {
    return 'N/A'
  }

  const hours = Math.floor(uptimeSeconds / SECONDS_PER_HOUR)
  const minutes = Math.floor((uptimeSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)
  const seconds = uptimeSeconds % SECONDS_PER_MINUTE

  if (hours > 0) {
    return `${String(hours)}h ${String(minutes)}m ${String(seconds)}s`
  }
  if (minutes > 0) {
    return `${String(minutes)}m ${String(seconds)}s`
  }
  return `${String(seconds)}s`
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null) {
    return 'N/A'
  }

  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= BYTES_PER_KIB && unitIndex < units.length - 1) {
    size /= BYTES_PER_KIB
    unitIndex++
  }

  return `${size.toFixed(1)} ${units[unitIndex] ?? ''}`
}
